import React from 'react'
import clsx from 'clsx'
import { People } from 'iconsax-react'
import { useTheme } from '@mui/material'
import { useEnvironment } from '@SonarCX/providers/EnvironmentProvider'
import useAccess, { PERMS } from '@SonarCX/hooks/useAccess'
import Dropdown from '@SonarCX/components/atoms/Dropdown'

interface TeamSectionProps {
  classes: any
  teams: Array<APITeam>
  values: any
  canEdit: boolean
  handleChange: Function
  isCreating?: boolean
}

enum PermissionLevel {
  admin = 'admin',
  update = 'update',
  read = 'read'
}

const TeamSection: React.FC<TeamSectionProps> = ({
  classes,
  teams,
  values,
  canEdit,
  handleChange,
  isCreating
}) => {
  const theme = useTheme()
  const hasPermission = useAccess()
  const { user: currentUser } = useEnvironment()
  const [permission, setPermission] = React.useState<null | PermissionLevel>(
    null
  )

  React.useEffect(() => {
    if (hasPermission(PERMS.TasksFull)) {
      setPermission(PermissionLevel.admin)
      return
    }

    if (canEdit) {
      const isCurrentTeamLead = teams
        .find(team => team.id === values?.team_id)
        ?.leaders.includes(currentUser?.id)
      const leadsMoreTeams = teams.filter(team =>
        team.leaders.includes(currentUser?.id)
      ).length

      //if no value but leads a team, let them select it
      if (!values?.team_id && leadsMoreTeams > 0) {
        setPermission(PermissionLevel.update)
        return
      }
      //if is part of a team, but leads the current one and more then let them edit
      if (isCurrentTeamLead && leadsMoreTeams > 1) {
        setPermission(PermissionLevel.update)
        return
      }
    }

    setPermission(PermissionLevel.read)
  }, [canEdit, teams, values, currentUser, hasPermission])

  return (
    <div className={clsx(classes.center, classes.label, classes.mobile)}>
      <span className={clsx(classes.center, classes.label)}>
        <People
          size={theme.size.base}
          variant='Bulk'
          color={theme.palette.primary.main}
        />
        TEAM
      </span>

      <Dropdown
        options={teams.map(({ id }) => id)}
        renderOption={id => teams.find(team => team.id === id)?.name || ''}
        disabledOptions={
          isCreating
            ? teams.map(({ id, members }) => {
                if (!members.find(member => member === currentUser?.id)) {
                  return id
                }
                return null
              })
            : permission === PermissionLevel.admin
            ? []
            : teams.map(({ id, leaders }) => {
                if (!leaders.find(leader => leader === currentUser?.id)) {
                  return id
                }
                return null
              })
        }
        selected={values.team_id}
        onSelect={id => handleChange('team_id')({ target: { value: id } })}
        hideAdd
        stringOnly
        disabled={isCreating ? false : permission === PermissionLevel.read}
      >
        <div className={classes.center}>
          <div data-testid='team'>
            {teams.find(({ id }) => id === values.team_id)?.name ||
              'Unassigned'}
          </div>
        </div>
      </Dropdown>
    </div>
  )
}

export default TeamSection
